import ServerUtils from '@root/core/src/api/server-utils';

export async function agentPasswordReset(params) {
  const { data } = await ServerUtils.unauthenticatedPatch(
    '/agent_api/password_reset',
    {
      body: {
        ...params,
      },
      expectedResponse: [200],
      expectedErrorResponses: [401, 422],
    });

  return data;
}

export async function sendForgotPassword(params) {
  const { data } = await ServerUtils.unauthenticatedPost(
    '/agent_api/password_reset',
    {
      body: {
        ...params,
      },
      expectedResponse: [200, 401],
      expectedErrorResponses: [422],
    });

  return data;
}

export async function configureAuthenticator(params) {
  const { data } = await ServerUtils.unauthenticatedPost(
    '/agent_api/sessions/configure_authenticator',
    {
      body: {
        ...params,
      },
      expectedResponse: [201],
      expectedErrorResponses: [401],
    });

  return data;
}

export async function validateSessionToken() {
  const { data } = await ServerUtils.authenticatedGet(
    '/agent_api/sessions/validate_token',
    {
      expectedResponse: [200, 401],
    }
  );

  return data;
}

export async function expireSessionToken() {
  const { data } = await ServerUtils.authenticatedPost(
    '/agent_api/sessions/expire_session',
    {
      expectedResponse: [200, 401],
    }
  );

  return data;
}

export async function sessionContext() {
  const { data } = await ServerUtils.authenticatedGet(
    '/agent_api/sessions/session_context',
    {
      expectedResponse: [200],
      expectedErrorResponses: [401],
    }
  );

  return data;
}

export async function loginWithEmail(params) {
  const { data } = await ServerUtils.unauthenticatedPost(
    '/agent_api/sessions',
    {
      body: {
        ...params,
      },
      expectedResponse: [200, 201, 400],
      expectedErrorResponses: [401],
    }
  );

  return data;
}

export async function sendOtpEmail(params) {
  return await ServerUtils.unauthenticatedPost(
    '/agent_api/sessions/send_otp_email',
    {
      body: {
        ...params,
      },
      expectedResponse: [200],
      expectedErrorResponses: [401, 422],
    }
  );
}

export async function validateOtp(params) {
  const { data } = await ServerUtils.unauthenticatedPost(
    '/agent_api/sessions/validate_otp',
    {
      body: {
        ...params,
      },
      expectedResponse: [201],
      expectedErrorResponses: [400, 401, 422],
    }
  );

  return data;
}

export async function createEstimatedQuote(params) {
  const results = await ServerUtils.authenticatedPost(
    '/agent_api/cp_api_proxy/v3/quoting/quote',
    {
      body: {
        ...params,
      },
      expectedResponse: [201, 400, 422, 401],
    }
  );

  return results;
}

export async function postEstimatedQuoteBridgedEvent(params) {
  try {
    const { data } = await ServerUtils.authenticatedPost(
      '/agent_api/estimated_quote_bridged',
      {
        body: {
          ...params,
        },
        expectedResponse: [200, 400],
      });

    return data;
  } catch (error) {
    if (!(error instanceof TypeError)) {
      throw error;
    }
  }
}

export async function postAgentPolicyPurchasedEvent(params) {
  try {
    const { data } = await ServerUtils.authenticatedPost(
      '/agent_api/policies/policy_bound',
      {
        body: {
          ...params,
        },
        expectedResponse: [200, 400],
      }
    );
    return data;
  } catch (error) {
    if (!(error instanceof TypeError)) {
      throw error;
    }
  }
}

export async function getEstimatedQuote(estimated_quote_id) {
  const results = await ServerUtils.authenticatedGet(
    `/agent_api/cp_api_proxy/v3/quoting/quote/${estimated_quote_id}`,
    {
      // eslint-disable-next-line
      expectedResponse: [200, 401, 404, 504],
    }
  );

  return results;
}

export async function findEstimatedQuote(params, { requireSuccessfulResponse = false } = {}) {
  const { data } = await ServerUtils.authenticatedGet(
    '/agent_api/cp_api_proxy/v3/agency/quotes',
    {
      query: {
        ...params,
      },
      trimNullQueryParams: true,
      // eslint-disable-next-line
      expectedResponse: requireSuccessfulResponse ? [200] : [200, 400, 401, 404],
    }
  );

  return data;
}

export async function patchEstimatedQuote(estimated_quote_id, params) {
  const { data } = await ServerUtils.authenticatedPatch(
    `/agent_api/cp_api_proxy/v3/quoting/quote/${estimated_quote_id}`,
    {
      body: {
        ...params,
      },
      expectedResponse: [200, 401, 425],
    }
  );

  return data;
}

export async function postCreatePrefill(estimated_quote_id) {
  const { data } = await ServerUtils.authenticatedPost(
    `/agent_api/cp_api_proxy/v3/quoting/quote/${estimated_quote_id}/prefill`,
    {
      expectedResponse: [201, 401, 422],
    }
  );

  return data;
}
export async function getPrefill(estimated_quote_id) {
  try {
    const { rawResponse } = await ServerUtils.authenticatedGet(
      `/agent_api/cp_api_proxy/v3/quoting/quote/${estimated_quote_id}/prefill`,
      {
        expectedResponse: [200, 204, 401, 504],
      }
    );

    return rawResponse;
  } catch (error) {
    if (!(error instanceof TypeError)) {
      throw error;
    }
  }
}

export async function sendLog(params) {
  try {
    // params are:
    //   severity: 'info' | 'error' | 'warn'
    //   message: log message to be recorded
    const rawResponse = await ServerUtils.authenticatedPost(
      '/agent_api/client_log',
      {
        body: {
          ...params,
        },
        expectedResponse: [200, 422],
      }
    );
    return rawResponse;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error sending log to server:', error);
  }
}

export async function existingUser(email) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${encodeURIComponent(email)}/exists`, {
    expectedResponse: [200, 401],
  });

  return data;
}

export async function findUser(email) {
  const { data, rawResponse } = await ServerUtils.authenticatedPost('/agent_api/users/find', {
    body: {
      email,
    },
    expectedResponse: [200, 204, 401, 403],
  });

  return {
    ...data,
    rawResponse,
  };
}

export async function findUserById(userId) {
  const { data } = await ServerUtils.authenticatedGet('/agent_api/users/find_by_id', {
    query: {
      userId,
    },
    expectedResponse: [200, 204, 401],
  });

  return data;
}

export async function findUserByQuoteId(quoteId) {
  const { data } = await ServerUtils.authenticatedGet('/agent_api/users/find_by_quote_id', {
    query: {
      quoteId,
    },
    expectedResponse: [200, 204, 401],
  });

  return data;
}

export async function syncEstimatedQuoteWithUser(estimatedQuoteId) {
  const { data } = await ServerUtils.authenticatedPost('/agent_api/users/sync_estimated_quote_with_user', {
    body: {
      estimatedQuoteId,
    },
    expectedResponse: [200, 401, 422],
  });

  return data;
}

export async function updateUserPhoneNumber(userId, phoneNumber) {
  const { data } = await ServerUtils.authenticatedPost(`/agent_api/users/${userId}/update_phone_number`, {
    body: {
      phoneNumber,
    },
    expectedResponse: [200, 422, 401],
  });

  return data;
}

export async function updateUserFullName(userId, firstName, lastName) {
  const { data } = await ServerUtils.authenticatedPost(`/agent_api/users/${userId}/update_full_name`, {
    body: {
      firstName,
      lastName,
    },
    expectedResponse: [200, 422, 401],
  });

  return data;
}

export async function triageToClassic(userId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/triage_to_classic`, {
    expectedResponse: [200, 401],
  });

  return data;
}

export async function classicOnlyMarketCheck(market) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/markets/${market.toUpperCase()}/classic_only`, {
    expectedResponse: [200, 401],
  });

  return data;
}

export async function allowListedMarkets() {
  const { data } = await ServerUtils.authenticatedGet('/agent_api/markets/allow_listed_markets', {
    expectedResponse: [200, 401],
  });

  return data;
}

export async function createUser(userParams) {
  const { data } = await ServerUtils.authenticatedPost('/agent_api/users', {
    body: userParams,
    expectedResponse: [201, 400, 409, 401],
  });

  return data;
}

export async function fetchProfileRules(market, userId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/markets/${market}/rules`, {
    query: {
      userId,
    },
  });

  return data;
}

export async function sendAppLink(userId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/download_app`, {
    expectedResponse: [200, 401],
  });

  return data;
}

export async function sendTestDriveLinkSms(userId) {
  const { data } = await ServerUtils.authenticatedPost(`/agent_api/users/${userId}/send_test_drive_link`, {
    expectedResponse: [200, 401],
  });

  return data;
}

export async function triggerPrefill(userId, prefillParams) {
  const { data } = await ServerUtils.authenticatedPost(`/agent_api/users/${userId}/prefills`, {
    body: prefillParams,
    expectedResponse: [201, 401],
    expectedErrorResponses: [400],
  });

  return data;
}

export async function fetchPrefill(userId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/prefills`, {
    expectedResponse: [200, 204, 400, 401],
  });

  return data;
}

export async function triggerQuote(userId, params) {
  const { data } = await ServerUtils.authenticatedPost(`/agent_api/users/${userId}/quotes`, {
    body: params,
    expectedResponse: [201, 401],
    expectedErrorResponses: [400, 422],
  });

  return data;
}

export async function fetchQuote(userId, ratingRequestId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/quotes?rating_request_id=${ratingRequestId}`, {
    expectedResponse: [200, 204, 400, 401],
  });

  return data;
}

export async function fetchQuoteCoverages(userId, ratingRequestId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/quote_coverages?rating_request_id=${ratingRequestId}`, {
    expectedResponse: [200, 204, 400, 401],
  });
  return data;
}

export async function fetchQuotingRules(userId, market) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/quoting_rules`, {
    query: {
      market,
    },
  });

  return data;
}

export async function createCustomQuote(userId, params) {
  const { data } = await ServerUtils.authenticatedPost(`/agent_api/users/${userId}/quotes/custom`, {
    body: params,
    expectedResponse: [201, 400, 422, 401],
  });

  return data;
}

export async function emailQuoteToUser(userId, quoteId, billingCycle) {
  return await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/quotes/${quoteId}/email_to_user`, {
    query: { invoicePeriod: billingCycle },
    expectedResponse: [200, 401],
  });
}

export async function acceptConsent(userId) {
  return await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/accept_consent`, {
    expectedResponse: [200, 401],
  });
}

export async function validateLicense(licenseParams) {
  const { data } = await ServerUtils.authenticatedPost('/agent_api/users/validate_license', {
    body: licenseParams,
    expectedResponse: [200, 401],
    expectedErrorResponses: 422,
  });

  return data;
}

export async function enableOneProduct(userId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/users/${userId}/enable_one_product`, {
    expectedResponse: [200, 401],
    expectedErrorResponses: 422,
  });

  return data;
}

export async function resetMFA(email) {
  const { data, rawResponse } = await ServerUtils.authenticatedPost('/agent_api/reset_mfa_for_agent', {
    body: { email },
    expectedResponse: [200, 204],
    expectedErrorResponses: 401,
  });

  return {
    data,
    rawResponse,
  };
}

export async function createOrUpdateHotLeadRecord(userId, agentId, active, followUpAppointment) {
  const { data, rawResponse } = await ServerUtils.authenticatedPatch('/agent_api/agent_hot_leads/create_or_update', {
    body: {
      userId,
      agentId,
      active,
      followUpAppointment,
    },
    expectedResponse: [200, 204, 401],
    expectedErrorResponses: [422],
  });

  return {
    data,
    rawResponse,
  };
}

export async function fetchRatingVariableDifference(ratingRequestId, userId) {
  const { data } = await ServerUtils.authenticatedGet(`/agent_api/rating_variable_difference?rating_request_id=${ratingRequestId}&user_id=${userId}`, {
    expectedResponse: [200, 401],
  });

  return data;
}

export async function fetchEppoExperiment(abTestName) {
  const { data } = await ServerUtils.authenticatedGet('/agent_api/eppo_experiment', {
    query: {
      ab_test_name: abTestName,
    },
    expectedResponse: [200],
  });

  return data;
}

export async function createQuoteFromPolicy(policyNumber) {
  const { data } = await ServerUtils.authenticatedPost(
    `/agent_api/cp_api_proxy/v3/policies/policy/${policyNumber}/quote`,
    {
      expectedResponse: [201],
      expectedErrorResponses: [401, 422],
    }
  );

  return data;
}

export async function authenticateAgent(params) {
  const { data } = await ServerUtils.authenticatedGet(
    '/agent_api/authenticate',
    {
      query: {
        agent_id: params.agentId,
        token: params.token,
        quote_id: params.quoteId,
      },
      expectedResponse: [200],
      expectedErrorResponses: [401],
    }
  );

  return data;
}

export async function checkAuthorization(params) {
  const { data } = await ServerUtils.authenticatedGet(
    '/agent_api/agent_authorization/is_current_agent_authorized',
    {
      query: {
        resource: params.resource,
        market: params.market,
      },
      expectedResponse: [200],
      expectedErrorResponses: [401],
    }
  );

  return data;
}
